import { Component, OnInit, AfterViewInit, ViewChild, inject, OnDestroy, signal, computed, output, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, debounceTime, combineLatest, switchMap, BehaviorSubject, startWith, of } from 'rxjs';
import {
  AdminUserListMode,
  AdminPortalUserMode,
  adminUserTypeQueryParams,
  adminUsersListNavigationExtras,
  adminUsersTableId,
  isAdminPortalUserMode,
  parseAdminUserListModeFromQuery,
} from '../admin-user-list-mode.model';
import { PrimeNgDataTableComponent } from '../../../shared/components/prime-ng-data-table';
import { DatatableEvent, DatatableColumn, DatatableOptions, LazyLoadEvent, DataFilterConfig, LabelFilterConfig, TextFilterConfig } from '../../../core/models/datatable.models';
import { MoreActionsItem } from '../../../shared/components/datatable/more-actions-menu/more-actions-menu.models';
import { ConfirmationModalComponent, ConfirmationModalConfig, ConfirmationModalEvent } from '../../../shared/components/confirmation-modal';
import { AdminUsersService } from '../../../core/services/admin-users.service';
import { PortalUsersService } from '../../../core/services/portal-users.service';
import { RoleService } from '../../../core/services/role.service';
import { DatatableFilterService } from '../../../core/services/datatable-filter.service';
import { PermissionService } from '../../../core/services/permission.service';
import { AuthService } from '../../../core/services/auth.service';
import { SYSTEM_PERMISSION_LIST } from '../../../shared/constants/permissions.constants';
import { Role } from '../../../core/models/admin-users.model';
import { PaginatedUsersRequest } from '../../../core/models/admin-users.model';
import type { PaginatedPortalUsersRequest } from '../../../core/models/portal-users.model';
import { formatPhoneNumber } from '../../../shared/utils/phone-number.util';
import { formatApiCalendarDateNumeric } from '../../../shared/utils/date.util';
import { absoluteHrefForCommands, navigateFromPointerEvent } from '../../../shared/utils/in-app-navigation.util';
import { buildRouteWithTenant } from '../../../shared/utils/route.util';
import { getTradePartnerDisplayName } from '../admin-client-user.util';

/** Display labels for GET /api/shard/users/paginated `status` query (mapped to backend enums). */
const ADMIN_USERS_STATUS_FILTER_LABELS: string[] = [
  'Active',
  'Invited',
  'Inactive',
  'Archived',
  'Restricted'
];

import { UpdateUserRequest } from '../../../core/models/admin-users.model';
import { TextFilterEvent } from '../../../core/models/datatable.models';
import { FloatingToolbarAction } from '../../../core/models/floating-toolbar.models';
import { ActionFeedbackService } from '../../../core/services/action-feedback.service';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [CommonModule, PrimeNgDataTableComponent, ConfirmationModalComponent],
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss']
})
export class AdminUsersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(PrimeNgDataTableComponent) datatableComponent!: PrimeNgDataTableComponent;

  /** Internal (API), client, or trade-partner user list (parent view-mode toggle). */
  readonly listMode = input<AdminUserListMode>('internal');
  readonly tableId = computed(() => adminUsersTableId(this.listMode()));

  /** Total user count for the admin page header (toolbar is rendered in parent). */
  readonly usersTotalChange = output<number>();
  /** Emitted when table clears filters so the parent search field can reset. */
  readonly headerSearchReset = output<void>();
  /** Role/Status filter chips active state for the admin shell filter bar. */
  readonly textFiltersShellStateChange = output<{ role: boolean; status: boolean }>();

  // Pagination and state
  currentPage = 1;
  currentFilter = '';
  currentSearchTerm = '';
  currentSortColumn = 'fullName'; // Changed from 'firstName' to match the column key
  currentSortDirection: 'asc' | 'desc' = 'asc';
  isLoading = false;
  currentStatusFilter = ''; // Add status filter property

  // Data - converted to signals for PrimeNG datatable
  readonly users = signal<any[]>([]);
  readonly totalEntries = signal(0);
  readonly hasNextPage = signal(true); // Track if there are more pages to load
  readonly accumulatedUsers = signal<any[]>([]); // Track all loaded users for lazy loading
  readonly loading = signal(false);

  // Additional properties for PrimeNG datatable
  readonly selectedRows = signal<number[]>([]);
  readonly filterOptions: { [key: string]: string[] } = {
    status: [...ADMIN_USERS_STATUS_FILTER_LABELS]
  };
  readonly totalEntriesComputed = computed(() => this.users().length);

  // Confirmation modal properties
  isDeleteModalOpen = false;
  isArchiveModalOpen = false;
  isArchiving = false; // Add loading state for archive operation
  deleteModalConfig: ConfirmationModalConfig = {
    icon: '',
    title: '',
    description: '',
    confirmButtonText: ''
  };
  archiveModalConfig: ConfirmationModalConfig = {
    icon: '',
    title: '',
    description: '',
    confirmButtonText: ''
  };
  userToDelete: any = null;
  userToArchive: any = null;

  // Table configuration - converted to computed signals for PrimeNG datatable
  readonly tableColumns = computed((): DatatableColumn[] => this.buildTableColumns(this.listMode()));

  readonly tableOptions: DatatableOptions = {
    pageSize: 20,
    lazyLoading: true,
    searchable: true,
    showColumnManagement: true,
    showMoreActions: true,
    showSearch: true,
    showFilter: true,
    showColumnVisibility: true,
    showFloatingToolbar: true
  };

  /** Batch actions when one or more users are selected (same pattern as dashboard `floatingToolbarActions`). */
  readonly floatingToolbarActions = computed((): FloatingToolbarAction[] => {
    const roleOrRevoke: FloatingToolbarAction = isAdminPortalUserMode(this.listMode())
      ? {
          label: 'Batch Revoke',
          action: 'batchRevoke',
          icon: 'assets/icons/remove-circle.svg',
        }
      : {
          label: 'Change Role',
          action: 'changeRole',
          icon: 'assets/icons/sync.svg',
        };

    return [
      { label: 'Send Batch Invite', action: 'sendBatchInvite', icon: 'assets/icons/email/send_blue.svg' },
      roleOrRevoke,
      { label: 'Change Status', action: 'changeStatus', icon: 'assets/icons/launch.svg' },
    ];
  });

  readonly dataFilters: DataFilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['All', ...ADMIN_USERS_STATUS_FILTER_LABELS],
      singleSelect: true,
      showSearch: false, // Hide search bar for status filter
      showSelectionCount: false, // Hide "X selected" count for status filter
      defaultValue: 'All' // Set "All" as the default value
    }
  ];

  // Text filter configuration for admin users table - Role and Status
  readonly textFilters: TextFilterConfig[] = [
    {
      key: 'role',
      label: 'Role',
      icon: 'assets/icons/filters/local_offer.svg'
    },
    {
      key: 'status',
      label: 'Status',
      icon: 'assets/icons/filters/status.svg'
    }
  ];

  // Label filter configuration for role filtering - removed, using text filters instead
  labelFilterConfig: LabelFilterConfig | undefined = undefined;

  // Cache for roles to enable roleId lookup
  private rolesCache: Role[] = [];

  // Add filter state tracking for multiple active filters
  private activeFilters = new Map<string, string>(); // filterKey -> filterValue
  private originalUsers: any[] = []; // Store original unfiltered data

  private destroy$ = new Subject<void>();

  // Filter change streams for different filter types
  private statusFilterSubject = new BehaviorSubject<string>('');
  private roleFilterSubject = new BehaviorSubject<number | null>(null);
  private searchFilterSubject = new BehaviorSubject<string>('');
  private sortFilterSubject = new BehaviorSubject<{ column: string, direction: 'asc' | 'desc' }>({ column: 'fullName', direction: 'asc' as const });
  private pageFilterSubject = new BehaviorSubject<number>(1);

  // Combined filter stream that handles all filter changes
  private combinedFilterStream$ = combineLatest([
    this.statusFilterSubject.pipe(startWith('')),
    this.roleFilterSubject.pipe(startWith(null)),
    this.searchFilterSubject.pipe(startWith('')),
    this.sortFilterSubject.pipe(startWith({ column: 'fullName', direction: 'asc' as const })),
    this.pageFilterSubject.pipe(startWith(1))
  ]).pipe(
    debounceTime(300), // Wait 300ms after last filter change
    switchMap(([status, roleId, search, sort, page]) => {
      const mode = this.listMode();
      if (isAdminPortalUserMode(mode)) {
        return this.makePortalApiCall(mode, roleId, search, sort, page);
      }
      return this.makeApiCall(status, roleId, search, sort, page);
    }),
    takeUntil(this.destroy$)
  );

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly actionFeedback = inject(ActionFeedbackService);
  private readonly authService = inject(AuthService);

  readonly addUserNavigationCommands = computed(() => buildRouteWithTenant(this.router, ['admin', 'users', 'add-user']));

  userRowDetailHref = (row: { id: number }): string | null => {
    if (row?.id == null) {
      return null;
    }
    const commands = buildRouteWithTenant(this.router, ['admin', 'users', 'edit-user', String(row.id)]);
    const queryParams = adminUserTypeQueryParams(this.listMode());
    return absoluteHrefForCommands(
      this.router,
      commands,
      queryParams ? { queryParams } : undefined
    );
  };

  constructor(
    private adminUsersService: AdminUsersService,
    private portalUsersService: PortalUsersService,
    private roleService: RoleService,
    private filterService: DatatableFilterService,
    private permissionService: PermissionService
  ) {
    this.loading.set(true);
    // Subscribe to the combined filter stream
    this.combinedFilterStream$.subscribe({
      next: (result) => {
        
        // Update component state with the response
        this.totalEntries.set(result.totalEntries);
        this.usersTotalChange.emit(result.totalEntries);
        this.hasNextPage.set(result.hasNextPage);
        // Handle data based on whether it's a new filter/sort or lazy loading
        if (this.currentPage === 1) {
          // First page or filter/sort change - replace all data
          this.accumulatedUsers.set(result.users);
          this.users.set(result.users);
          // Update PrimeNG datatable component with fresh data
          if (this.datatableComponent) {
            this.datatableComponent.updateLoadedData(result.users, result.totalEntries);
          }
        } else {
          // Lazy loading - append new data
          this.accumulatedUsers.set([...this.accumulatedUsers(), ...result.users]);
          this.users.set(this.accumulatedUsers());
          // Update PrimeNG datatable component by appending data
          if (this.datatableComponent) {
            this.datatableComponent.addMoreData(result.users);
          }
        }
        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
        this.handleApiError();
      }
    });
  }

  ngOnInit() {
    this.filterService.initializeFilters(this.filtersTableId());

    // Load roles for the role filter
    this.loadRoles();

    // Check for action result query parameters
    this.route.queryParams.subscribe(params => {
      if (params['action'] === 'delete' && params['count'] && params['itemType'] === 'user') {
        // Show action result toolbar after successful deletion
        setTimeout(() => {
          if (this.datatableComponent) {
            this.datatableComponent.showActionResultAfterConfirmation('delete', parseInt(params['count']));
          }
        }, 100);

        const userTypeParams = adminUsersListNavigationExtras(
          parseAdminUserListModeFromQuery(params['userType'])
        ).queryParams ?? {};
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            action: null,
            count: null,
            itemType: null,
            ...userTypeParams,
          },
          replaceUrl: true,
        });
      }
    });
  }

  ngAfterViewInit() {
    // Trigger initial data load by emitting current filter values
    this.statusFilterSubject.next(this.currentStatusFilter);
    this.roleFilterSubject.next(null);
    this.searchFilterSubject.next(this.currentSearchTerm);
    this.sortFilterSubject.next({ column: this.currentSortColumn, direction: this.currentSortDirection });
    this.pageFilterSubject.next(this.currentPage);
    this.emitTextFiltersShellState();
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load roles from the API and set them in the datatable for the role filter
   */
  private loadRoles(): void {
    this.roleService.fetchAllRoles().subscribe({
      next: (roles) => {
        if (!roles?.length) {
          this.rolesCache = [];
          this.filterOptions['role'] = [];
          return;
        }
        this.rolesCache = roles;
        this.filterOptions['role'] = roles.map((role) => role.name);
      },
      error: (error) => {
        console.error('Error loading roles:', error);
      },
    });
  }

  /**
   * Apply all currently active filters to the datatable
   */
  private applyAllActiveFilters(): void {
    // Update the filter subjects with current values to trigger API call
    this.currentPage = 1;

    // Update status filter subject and shard filter state (merged into paginated GET request)
    const currentStatus = this.activeFilters.get('status') || '';
    if (currentStatus) {
      this.filterService.updateFilter(this.filtersTableId(), 'status', currentStatus);
    } else {
      this.filterService.clearFilter(this.filtersTableId(), 'status');
    }
    this.statusFilterSubject.next(currentStatus);

    // Update role filter subject
    const currentRole = this.activeFilters.get('role') || null;

    if (currentRole) {
      // Find the role ID from the role name
      const role = this.rolesCache.find((r: any) => r.name === currentRole);

      const roleId = role ? role.roleId : null;

      this.roleFilterSubject.next(roleId);
    } else {
      this.roleFilterSubject.next(null);
    }

    // Update page filter subject to trigger reload
    this.pageFilterSubject.next(1);

    this.emitTextFiltersShellState();
  }

  private emitTextFiltersShellState(): void {
    this.textFiltersShellStateChange.emit({
      role: this.activeFilters.has('role'),
      status: this.listMode() === 'internal' && this.activeFilters.has('status'),
    });
  }

  /** Called when the parent users view-mode toggle changes. */
  onListModeChanged(): void {
    this.resetForListModeChange();
  }

  private filtersTableId(): string {
    return adminUsersTableId(this.listMode());
  }

  private resetForListModeChange(): void {
    this.currentPage = 1;
    this.currentStatusFilter = '';
    this.currentSearchTerm = '';
    this.currentSortColumn = 'fullName';
    this.currentSortDirection = 'asc';
    this.hasNextPage.set(true);

    // Clear parent + datatable row state before loading so old rows are not shown under the loader
    this.accumulatedUsers.set([]);
    this.users.set([]);
    this.totalEntries.set(0);
    this.usersTotalChange.emit(0);
    this.selectedRows.set([]);
    if (this.datatableComponent) {
      this.datatableComponent.updateLoadedData([], 0);
      this.datatableComponent.resetPaginationState();
    }

    this.clearActiveFilters();
    this.filterService.initializeFilters(this.filtersTableId());
    this.filterService.clearFilters(this.filtersTableId());
    this.filterService.updateFilter(this.filtersTableId(), 'roleId', null);
    this.loading.set(true);
    this.headerSearchReset.emit();
    this.emitTextFiltersShellState();
    this.statusFilterSubject.next('');
    this.roleFilterSubject.next(null);
    this.searchFilterSubject.next('');
    this.sortFilterSubject.next({ column: 'fullName', direction: 'asc' });
    this.pageFilterSubject.next(1);
  }

  private buildTableColumns(mode: AdminUserListMode): DatatableColumn[] {
    const base: DatatableColumn[] = [
      {
        key: 'fullName',
        label: 'FULL NAME',
        sortable: true,
        width: 'auto',
        visible: true,
        render: (_value: unknown, row: unknown) => this.renderUserWithProfilePicture(row),
      },
      {
        key: 'roleName',
        label: 'ROLE',
        sortable: true,
        width: 'auto',
        visible: true,
      },
      {
        key: 'email',
        label: 'EMAIL',
        sortable: true,
        width: 'auto',
        visible: true,
        render: (_value: unknown, row: unknown) => this.renderEmail(row),
      },
      {
        key: 'contactNumber',
        label: 'PHONE NUMBER',
        sortable: false,
        width: 'auto',
        visible: true,
        render: (_value: unknown, row: unknown) => this.renderPhoneNumber(row),
      },
    ];

    if (mode === 'internal') {
      base.push({
        key: 'dateLastLogin',
        label: 'LAST ACTIVE STATUS',
        sortable: true,
        width: 'auto',
        visible: true,
        cssClass: 'admin-users-status-cell',
        render: (_value: unknown, row: unknown) => this.renderUserStatus(row),
      });
    } else {
      base.push({
        key: 'portalAccessStatus',
        label: 'PORTAL ACCESS',
        sortable: false,
        width: 'auto',
        visible: true,
        cssClass: 'admin-users-portal-access-cell text-center',
        render: (_value: unknown, row: any) => this.renderPortalAccess(row),
      });
    }

    base.push({
      key: 'actions',
      label: '',
      type: 'action',
      width: '60px',
      sortable: false,
      visible: true,
    });

    return base;
  }

  /** Apply Role/Status from the admin shell filter modals (inline text filters are hidden). */
  applyShellTextFilter(payload: { filterKey: 'role' | 'status'; value: string }): void {
    this.currentPage = 1;
    this.accumulatedUsers.set([]);
    this.onTextFilterChange({ filterKey: payload.filterKey, active: true, value: payload.value });
  }

  /** Clear role/status/search pipeline; same as datatable “Clear all filters”. */
  clearFiltersFromShell(): void {
    this.handleClearFilters();
  }

  getShellRoleOptionNames(): string[] {
    return [...(this.filterOptions['role'] ?? [])];
  }

  getShellStatusOptionLabels(): string[] {
    return [...ADMIN_USERS_STATUS_FILTER_LABELS];
  }

  getShellSelectedRoleName(): string {
    return this.activeFilters.get('role') ?? '';
  }

  getShellSelectedStatusLabel(): string {
    const backend = this.activeFilters.get('status');
    if (!backend) {
      return '';
    }
    return this.mapBackendStatusToFilterLabel(backend);
  }

  /**
   * Handle text filter changes for the new filter system
   */
  onTextFilterChange(event: TextFilterEvent) {
    switch (event.filterKey) {
      case 'role':
        this.filterByRole(event.value || '');
        break;
      case 'status':
        this.filterByStatus(event.value || '');
        break;
    }
  }

  /**
   * Filter by role using text filter - populated with data from old dropdown
   */
  private filterByRole(roleValues: string): void {
    // Update the active filters map
    if (roleValues && roleValues.trim() !== '' && roleValues !== 'all' && roleValues !== 'All') {
      this.activeFilters.set('role', roleValues);
    } else {
      this.activeFilters.delete('role');
    }

    // Apply all active filters
    this.applyAllActiveFilters();
  }

  /**
   * Filter by status using text filter - populated with data from table
   */
  private filterByStatus(statusValues: string): void {
    if (statusValues && statusValues.trim() !== '' && statusValues !== 'All') {
      const firstLabel = statusValues.split(',').map(s => s.trim()).filter(Boolean)[0] ?? statusValues.trim();
      const backendStatus = this.mapUserStatusToBackend(firstLabel);
      this.activeFilters.set('status', backendStatus);
    } else {
      this.activeFilters.delete('status');
    }

    this.applyAllActiveFilters();
  }

  /**
   * Clear all active filters
   */
  private clearActiveFilters(): void {
    this.activeFilters.clear();
    this.applyAllActiveFilters();
  }

  /**
   * Make an API call to fetch users based on current filters
   */
  private makeApiCall(status: string, roleId: number | null, searchTerm: string, sort: {column: string, direction: 'asc' | 'desc'}, page: number) {
    // Map column keys to API sort fields (same mapping as in handleSort)
    const sortMapping: { [key: string]: string } = {
      'fullName': 'firstName',
      'email': 'email',
      'roleName': 'roleName',
      'dateLastLogin': 'dateLastLogin'
    };

    const mappedSortBy = sortMapping[sort.column] || sort.column;

    const request: PaginatedUsersRequest = {
      pageNumber: page,
      pageSize: this.tableOptions.pageSize || 20,
      searchTerm: searchTerm,
      sortBy: mappedSortBy,
      sortOrder: sort.direction,
      internalOnly: true,
    };

    // Add status filter if it's a valid status
    if (status && status !== null && status !== undefined && status !== '') {
      request.status = status;
    }

    // Add roleId filter if it's a valid number
    if (roleId !== null && roleId !== undefined && roleId !== 0) {
      request.roleId = roleId;
    }

    // Add search term if it's not empty
    if (searchTerm && searchTerm.trim() !== '') {
      request.searchTerm = searchTerm;
    }

    return this.adminUsersService.loadUsersFromAPI(this.filtersTableId(), request, false);
  }

  /** GET /api/shard/users/client/paginated or /tradepartner/paginated */
  private makePortalApiCall(
    mode: AdminPortalUserMode,
    roleId: number | null,
    searchTerm: string,
    sort: { column: string; direction: 'asc' | 'desc' },
    page: number
  ) {
    const sortMapping: Record<string, string> = {
      fullName: 'firstName',
      email: 'email',
      roleName: 'roleName',
      contactNumber: 'phoneNumber',
    };
    const request: PaginatedPortalUsersRequest = {
      pageNumber: page,
      pageSize: this.tableOptions.pageSize || 20,
      searchTerm: searchTerm?.trim() || undefined,
      sortBy: sortMapping[sort.column] || sort.column,
      sortOrder: sort.direction,
    };
    if (roleId !== null && roleId !== undefined && roleId !== 0) {
      request.roleId = roleId;
    }
    return this.portalUsersService.loadPortalUsersFromAPI(mode, request);
  }

  /**
 * Handle API errors gracefully
 */
  private handleApiError() {
    this.isLoading = false;
    // You can add more error handling logic here, such as showing a toast notification
  }

  /**
   * Get user initials from first and last name
   */
  private getUserInitials(user: any): string {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    // If we have both names, use them
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }

    // If we only have one name, use it
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }

    // If no names are available, try to use email
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    // Last resort - use 'U' for Unknown
    return 'U';
  }

  /**
   * Render user with profile picture and initials fallback
   */
  private renderUserWithProfilePicture(user: any): string {
    // Use the original user data properties
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const fullName =
      this.listMode() === 'trade-partners'
        ? getTradePartnerDisplayName({
            nickname: user.nickname ?? '',
            firstName,
            lastName,
          })
        : `${firstName} ${lastName}`.trim();
    const profilePicture = user.profilePictureUrl || '';
    const initials = this.getUserInitials(user);

    if (profilePicture) {
      return `<div style="display: flex; align-items: center; gap: 12px;" class="user-name">
      <div class="profile-picture">
      <img src="${profilePicture}" alt="${fullName}" style="width: 100%; height: 100%; object-fit: cover;" /></div>
      <span class="user-name">${fullName}</span>
      </div>`;
    } else {
      return `<div style="display: flex; align-items: center; gap: 12px;" class="user-name">
      <div class="profile-initials">${initials}</div>
      <span class="user-name">${fullName}</span>
      </div>`;
    }
  }

  /**
   * Render email only
   */
  private renderEmail(user: any): string {
    const email = user.email || '-';
    return `<span class="user-email">${email}</span>`;
  }

  /**
   * Render formatted phone number only
   */
  private renderPhoneNumber(user: any): string {
    const contactNumber = user.contactNumber || '';
    const contactExt = user.contactExt || '';
    const formattedPhone = contactNumber ? formatPhoneNumber(contactNumber, contactExt) : '-';
    return `<span class="user-phone">${formattedPhone}</span>`;
  }

  /**
   * Render last-active column: pill badge (dot + label) + "on MM/DD/YYYY" (matches admin users design).
   */
  private renderUserStatus(user: any): string {
    const userStatus = user.userStatus || '';
    const dateLastLogin = user.dateLastLogin || '';

    let pillLabel = '—';
    let pillModifier: 'active' | 'inactive' | 'invited' | 'restricted' | 'archived' | 'unknown' = 'unknown';

    if (userStatus === 'EMAIL_NOT_CONFIRMED') {
      pillLabel = 'Invited';
      pillModifier = 'invited';
    } else if (userStatus === 'ACTIVE') {
      pillLabel = 'Active';
      pillModifier = 'active';
    } else if (userStatus === 'INACTIVE') {
      pillLabel = 'Inactive';
      pillModifier = 'inactive';
    } else if (userStatus === 'ARCHIVED') {
      pillLabel = 'Archived';
      pillModifier = 'archived';
    } else if (userStatus === 'RESTRICTED') {
      pillLabel = 'Restricted';
      pillModifier = 'restricted';
    } else if (userStatus) {
      pillLabel = userStatus;
      pillModifier = 'inactive';
    }

    let formattedDate = 'N/A';
    if (dateLastLogin) {
      formattedDate = formatApiCalendarDateNumeric(dateLastLogin) || 'N/A';
    }

    return `
      <div class="user-last-active-status">
        <span class="status-pill status-pill--${pillModifier}">
          <span class="status-pill__dot" aria-hidden="true"></span>
          <span class="status-pill__label">${pillLabel}</span>
        </span>
        <span class="last-active-on">on ${formattedDate}</span>
      </div>
    `;
  }

  /** Portal access badge for client / trade-partner lists (Figma). */
  private renderPortalAccess(user: { portalDisplayStatus?: string; portalAccessStatus?: string }): string {
    const raw = (user.portalDisplayStatus ?? user.portalAccessStatus ?? '').toLowerCase();
    if (raw === 'active') {
      return `
      <span class="status-pill status-pill--active">
        <span class="status-pill__dot" aria-hidden="true"></span>
        <span class="status-pill__label">Active</span>
      </span>
    `;
    }
    if (raw === 'pending') {
      return `
      <span class="status-pill status-pill--pending">
        <span class="status-pill__dot" aria-hidden="true"></span>
        <span class="status-pill__label">Pending Invite</span>
      </span>
    `;
    }
    if (raw === 'invited') {
      return `
      <span class="status-pill status-pill--invited">
        <span class="status-pill__dot" aria-hidden="true"></span>
        <span class="status-pill__label">Invited</span>
      </span>
    `;
    }
    if (raw === 'inactive') {
      return `
      <span class="status-pill status-pill--inactive">
        <span class="status-pill__dot" aria-hidden="true"></span>
        <span class="status-pill__label">Inactive</span>
      </span>
    `;
    }
    return '<span class="user-portal-access">—</span>';
  }

  /**
   * Row `id` from the admin users list matches the signed-in user's id.
   * Backend disallows users from deleting/archiving themselves; we hide those actions in the UI.
   */
  private isAdminListRowCurrentUser(user: { id?: number } | null | undefined): boolean {
    const currentId = this.authService.getCurrentUser()?.id;
    const rowId = user?.id;
    return currentId != null && rowId != null && Number(currentId) === Number(rowId);
  }

  // Dynamic more actions getter
  get moreActions(): MoreActionsItem[] {
    const isPortalList = isAdminPortalUserMode(this.listMode());

    // If we have a selected row, use its data to determine disabled state
    if (this.selectedRowData) {
      const isSelf = this.isAdminListRowCurrentUser(this.selectedRowData);

      const base: MoreActionsItem[] = [
        {
          label: 'Edit',
          action: 'edit'
        }
      ];
      if (!isSelf) {
        if (!isPortalList) {
          const isArchiveDisabled = ['EMAIL_NOT_CONFIRMED', 'RESTRICTED', 'ARCHIVED'].includes(
            this.selectedRowData.userStatus
          );
          base.push({
            label: 'Archive',
            action: 'archive',
            disabled: isArchiveDisabled
          });
        }
        base.push({
          label: 'Delete',
          action: 'delete'
        });
      }
      return base;
    }

    // Default actions when no row is selected
    if (isPortalList) {
      return [
        { label: 'Edit', action: 'edit' },
        { label: 'Delete', action: 'delete' }
      ];
    }

    return [
      { label: 'Edit', action: 'edit' },
      { label: 'Archive', action: 'archive' },
      { label: 'Delete', action: 'delete' }
    ];
  }

  // Track the currently selected row for the more actions menu
  selectedRowData: any = null;

  onTableEvent(event: DatatableEvent) {
    switch (event.type) {
      case 'loadMore':
        this.handleLoadMore(event.value);
        break;
      case 'sort':
        this.handleSort(event.value);
        break;
      case 'filter':
        // this.handleFilterChange(event.value);
        // Handle filtering
        this.currentPage =1;
        this.accumulatedUsers.set([]);
        this.onTextFilterChange(event.value);
        break;
      case 'search':
        this.handleSearch(event.value);
        break;
      case 'clearFilters':
        this.handleClearFilters();
        break;
      case 'add':
        this.handleAddUser();
        break;
      case 'rowAction':
        // Handle row action
        this.onMoreActionClick({action: event.value.action, rowData: event.value.rowData});
        break;
      case 'moreActionsMenuOpen':
        // Handle more actions menu opening - update actions based on row data
        this.onMoreActionClick({action: 'rowSelected', rowData: event.value});
        break;
      default:
        // Handle other event types if needed
        break;
    }
  }

  // PrimeNG datatable specific methods
  handleManualLazyLoad(event: LazyLoadEvent): void {
    // Handle manual lazy loading for PrimeNG datatable
    this.handleLoadMore({ currentCount: this.users().length, pageSize: this.tableOptions.pageSize || 20 });
  }

  onRowSelect(event: any): void {
    const userId = event.data?.id;
    if (userId) {
      this.selectedRows.set([userId]);
      this.selectedRowData = event.data;
    }
  }

  onRowUnselect(event: any): void {
    this.selectedRows.set([]);
    this.selectedRowData = null;
  }

  onSelectAll(event: any): void {
    // Admin users has multiselect = false, so this shouldn't be called
    // But we'll handle it just in case
    if (event.checked) {
      const allIds = this.users().map(user => user.id);
      this.selectedRows.set(allIds);
    } else {
      this.selectedRows.set([]);
    }
  }

  getSelectedRowsArray(): number[] {
    return this.selectedRows();
  }

  /**
   * Batch toolbar (same contract as dashboard {@link DashboardComponent.handleFloatingToolbarActionClick}).
   * Wire APIs when batch invite / role / status endpoints are available.
   */
  onFloatingToolbarActionClick(event: { action: string; selectedRows: any[] }): void {
    if (!this.hasUsersAddEditPermission()) {
      return;
    }
    const { action, selectedRows } = event;
    if (!selectedRows?.length) {
      return;
    }
    switch (action) {
      case 'sendBatchInvite':
        this.actionFeedback.showInfo('Send batch invite is not available yet.');
        break;
      case 'changeRole':
        this.actionFeedback.showInfo('Change role is not available yet.');
        break;
      case 'batchRevoke':
        this.actionFeedback.showInfo('Batch revoke is not available yet.');
        break;
      case 'changeStatus':
        this.actionFeedback.showInfo('Change status is not available yet.');
        break;
      default:
        break;
    }
  }

  onRowClick(event: { row: { id: number }; event?: MouseEvent }): void {
    if (event?.row?.id) {
      this.viewUserDetails(event.row, event.event);
    }
  }

  handleRowExpand(event: { row: { id: number }; event?: MouseEvent }): void {
    if (event?.row?.id) {
      this.viewUserDetails(event.row, event.event);
    }
  }

  private handleLoadMore(event: any) {
    const { currentCount, pageSize } = event;

    // Check if there are more pages to load
    if (!this.hasNextPage()) {
      return;
    }

    // Increment the current page for the next load
    this.currentPage++;
    this.pageFilterSubject.next(this.currentPage);
  }

  private handleSort(event: any) {
    const { sortField, sortOrder } = event;

    // Update the component's sort state to maintain consistency
    this.currentSortColumn = sortField;
    this.currentSortDirection = sortOrder === 1 ? 'asc' : 'desc';
    // Reset to page 1 when sorting
    this.currentPage = 1;
    this.pageFilterSubject.next(1);

    // Update the sort filter subject
    this.sortFilterSubject.next({ column: this.currentSortColumn, direction: this.currentSortDirection as 'asc' | 'desc' });
  }

  private handleFilterChange(filterValue: string) {
    // This method is no longer used for role filtering since we now use the label filter dropdown
    // Role filtering is handled by onLabelFilterChange method
    // No action needed - role filtering is handled elsewhere
  }

  private handleSearch(searchTerm: string) {
    this.currentSearchTerm = searchTerm;

    // Reset to first page when searching
    this.currentPage = 1;
    this.pageFilterSubject.next(1);

    // Update the search filter subject
    this.searchFilterSubject.next(searchTerm);

  }

  private handleClearFilters() {
    // Clear all filter states
    this.currentStatusFilter = '';
    this.currentSearchTerm = '';
    this.headerSearchReset.emit();
    this.clearActiveFilters(); // Clear active filters from the map

    // Clear filters in the filter service
    this.filterService.clearFilters(this.filtersTableId());

    this.filterService.updateFilter(this.filtersTableId(), 'roleId', null);

    // Reset all filter subjects
    this.statusFilterSubject.next('');
    this.roleFilterSubject.next(null);
    this.searchFilterSubject.next('');
    this.sortFilterSubject.next({ column: 'fullName', direction: 'asc' as const });
    this.pageFilterSubject.next(1);

    // Reset accumulated users since we're starting fresh
    this.accumulatedUsers.set([]);
    this.users.set([]);

    // Clear selection
    this.selectedRows.set([]);
    this.selectedRowData = null;

  }

  /**
   * Handle data filter changes
   */
  onDataFilterChange(filterValues: { [key: string]: any }) {
    // Update filters in the filter service
    this.filterService.updateFilters(this.filtersTableId(), filterValues);

    // Handle status filtering from expanded filters
    if (filterValues['status']) {
      this.handleStatusFilterChange(filterValues['status']);
    } else {
      // If no status filter, clear the status filter and trigger API call
      this.currentStatusFilter = '';
      this.statusFilterSubject.next('');
    }

    // Reset to first page
    this.currentPage = 1;
    this.pageFilterSubject.next(1);
  }

  private handleStatusFilterChange(statusValue: string) {
    let backendStatus: string | null = null;

    // If "All" is selected, don't send any status parameter
    if (statusValue && statusValue !== 'All') {
      // Map display status to backend status using the correct mapping
      backendStatus = this.mapUserStatusToBackend(statusValue);
    }

    this.currentStatusFilter = backendStatus || '';

    // Update the filter service
    this.filterService.updateFilter(this.filtersTableId(), 'status', backendStatus);

    // Reset to first page
    this.currentPage = 1;
    this.pageFilterSubject.next(1);

    // Update the status filter subject
    this.statusFilterSubject.next(backendStatus || '');
  }

  /**
   * Map user status display labels to backend API values
   */
  private mapUserStatusToBackend(displayStatus: string): string {
    const statusMapping: { [key: string]: string } = {
      Active: 'ACTIVE',
      Invited: 'EMAIL_NOT_CONFIRMED',
      Inactive: 'INACTIVE',
      Archived: 'ARCHIVED',
      Restricted: 'RESTRICTED',
      'Active User': 'ACTIVE',
      'Inactive User': 'INACTIVE',
      'Archived User': 'ARCHIVED',
      'Restricted User': 'RESTRICTED',
      'Email Not Confirmed': 'EMAIL_NOT_CONFIRMED'
    };

    return statusMapping[displayStatus] || displayStatus;
  }

  private mapBackendStatusToFilterLabel(backend: string): string {
    const reverse: { [key: string]: string } = {
      ACTIVE: 'Active',
      EMAIL_NOT_CONFIRMED: 'Invited',
      INACTIVE: 'Inactive',
      ARCHIVED: 'Archived',
      RESTRICTED: 'Restricted'
    };
    return reverse[backend] ?? '';
  }

  /**
   * Handle more action clicks
   */
  onMoreActionClick(event: { action: string; rowData: any }) {
    const { action, rowData } = event;

    switch (action) {
      case 'rowSelected':
        // Update the selected row data for dynamic moreActions
        this.selectedRowData = rowData;
        break;
      case 'edit':
        this.editUser(rowData);
        break;
      case 'view':
        this.viewUserDetails(rowData);
        break;
      case 'deactivate':
        this.deactivateUser(rowData);
        break;
      case 'resetPassword':
        this.resetUserPassword(rowData);
        break;
      case 'archive':
        // Show the archive confirmation modal
        this.showArchiveConfirmation(rowData);
        break;
      case 'delete':
        this.showDeleteConfirmation(rowData);
        break;
      default:
      // Unknown action - could add error handling here
    }
  }

  /**
   * Handle row click - navigate to view mode
   */
  onRowClickOld(event: { event: Event, row: any }) {
    this.viewUserDetails(event.row);
  }

  /**
   * Show archive confirmation for a single user
   */
  private showArchiveConfirmation(user: any) {
    if (isAdminPortalUserMode(this.listMode()) || this.isAdminListRowCurrentUser(user)) {
      return;
    }
    this.userToArchive = user;
    this.isArchiveModalOpen = true;
    this.isArchiving = false; // Reset loading state

    this.archiveModalConfig = {
      title: "You're About to Archive a User",
      subtitle: "This action will archive a user and remove their associated access.",
      description: '',
      showInfoWarning: false,
      aioDestructive: true,
      confirmButtonText: 'Yes, Archive',
      confirmButtonDisabled: this.isArchiving
    };
  }

  /**
   * Show delete confirmation for a single user
   */
  private showDeleteConfirmation(user: any) {
    if (this.isAdminListRowCurrentUser(user)) {
      return;
    }
    this.userToDelete = user;
    this.isDeleteModalOpen = true;
    this.deleteModalConfig = {
      title: "You're About to Delete a User",
      subtitle: `This action will permanently remove the user and their associated access.`,
      description: '',
      showInfoWarning: false,
      aioDestructive: true,
      confirmButtonText: 'Yes, Delete'
    };
  }

  /**
   * Handle archive confirmation
   */
  onArchiveConfirm(event: ConfirmationModalEvent) {
    if (this.userToArchive) {
      this.isArchiving = true; // Set loading state

      // Update modal config to show loading state
      this.archiveModalConfig = {
        ...this.archiveModalConfig,
        confirmButtonText: 'Archiving...',
        confirmButtonDisabled: true
      };

      // Create update data, handling potentially invalid phone numbers
      const updateData: UpdateUserRequest = {
        email: this.userToArchive.email,
        firstName: this.userToArchive.firstName,
        middleName: this.userToArchive.middleName || '',
        lastName: this.userToArchive.lastName,
        userStatus: 'ARCHIVED', // Only change this field
        disc: this.userToArchive.disc || ''
      };

      // Only add contact fields if they exist and are valid
      if (this.userToArchive.contactNumber && this.userToArchive.contactNumber.trim() !== '') {
        updateData.contactNumber = this.userToArchive.contactNumber;
      }
      if (this.userToArchive.contactExt && this.userToArchive.contactExt.trim() !== '') {
        updateData.contactExt = this.userToArchive.contactExt;
      }

      this.adminUsersService.updateUserById(this.userToArchive.id, updateData).subscribe({
        next: (response) => {
          // Show action result toolbar after successful archive
          if (this.datatableComponent) {
            this.datatableComponent.showActionResultAfterConfirmation('archive', 1);
          }

          // Reload data to reflect changes
          this.currentPage = 1;
          this.pageFilterSubject.next(1);

          // Close modal and reset loading state
          this.isArchiveModalOpen = false;
          this.isArchiving = false;
        },
        error: (error) => {
          console.error('Error archiving user:', error);

          // Close modal and reset loading state
          this.isArchiveModalOpen = false;
          this.isArchiving = false;
        }
      });
    }
  }

  /**
   * Handle archive cancellation
   */
  onArchiveCancel(event: ConfirmationModalEvent) {
    this.isArchiveModalOpen = false;
    this.userToArchive = null;
    this.isArchiving = false; // Reset loading state
  }

  /**
   * Handle delete confirmation
   */
  onDeleteConfirm(event: ConfirmationModalEvent) {

    if (this.userToDelete) {
      const userId = this.userToDelete.id ?? this.userToDelete.userId;
      const delete$ =
        this.listMode() === 'trade-partners'
          ? this.portalUsersService.deleteTradePartnerUser(userId, true)
          : this.listMode() === 'clients'
            ? this.portalUsersService.deleteClientUser(userId, true)
            : this.adminUsersService.deleteUser(String(userId), true);

      delete$.subscribe({
        next: () => {
          if (this.datatableComponent) {
            this.datatableComponent.showActionResultAfterConfirmation('delete', 1);
          }

          this.currentPage = 1;
          this.pageFilterSubject.next(1);

          setTimeout(() => {
            // Delayed check for action-result toolbar state
          }, 100);
        },
        error: (error) => {
          console.error('Delete user failed:', error);
        }
      });

      this.isDeleteModalOpen = false;
    }
  }

  /**
   * Handle delete cancellation
   */
  onDeleteCancel(event: ConfirmationModalEvent) {
    this.isDeleteModalOpen = false;
    this.userToDelete = null;
  }

  /**
   * Clear userToDelete after action-result toolbar is shown
   */
  onActionResultShown() {
    this.userToDelete = null;
    this.userToArchive = null;
  }

  /**
   * Edit user action
   */
  private editUser(user: any) {
    // Get current route to extract tenant ID
    const currentUrl = this.router.url;
    const tenantMatch = currentUrl.match(/\/([^\/]+)\/admin/);

    if (tenantMatch && user.id) {
      // Navigate to tenant-specific edit user page with edit mode parameter
      const qp: Record<string, string> = { mode: 'edit' };
      const userType = adminUserTypeQueryParams(this.listMode());
      if (userType) {
        Object.assign(qp, userType);
      }
      this.router.navigate([`/${tenantMatch[1]}/admin/users/edit-user/${user.id}`], {
        queryParams: qp,
      });
    } else {
      // No tenant ID found or no user ID - this shouldn't happen in production
      // Could add error handling here
    }
  }

  /**
   * View user details action
   */
  private viewUserDetails(user: { id: number }, ev?: MouseEvent) {
    const currentUrl = this.router.url;
    const tenantMatch = currentUrl.match(/\/([^\/]+)\/admin/);

    if (tenantMatch && user.id) {
      const commands = [tenantMatch[1], 'admin', 'users', 'edit-user', String(user.id)];
      const openNewTab = !!ev && (ev.button === 1 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey);
      if (openNewTab) {
        navigateFromPointerEvent(ev, this.router, commands);
        return;
      }
      const queryParams = adminUserTypeQueryParams(this.listMode());
      void this.router.navigate(commands, queryParams ? { queryParams } : undefined);
    }
  }

  /**
   * Deactivate user action
   */
  private deactivateUser(user: any) {
    // Deactivate user functionality will be implemented in future iterations
    // This could show a confirmation dialog and call an API
  }

  /**
   * Reset user password action
   */
  private resetUserPassword(user: any) {
    // Reset password functionality will be implemented in future iterations
    // This could show a confirmation dialog and call an API
  }

  /** Called from admin shell header search (toolbar is outside this component). */
  applyToolbarSearch(term: string): void {
    this.handleSearch(term);
  }

  /** Called from admin shell header Add User button. */
  openAddUser(): void {
    this.handleAddUser();
  }

  /**
   * Handle add user button click
   */
  private handleAddUser() {
    // Get current route to extract tenant ID
    const currentUrl = this.router.url;
    const tenantMatch = currentUrl.match(/\/([^\/]+)\/admin/);

    if (tenantMatch) {
      // Navigate to tenant-specific add user page
      const queryParams = adminUserTypeQueryParams(this.listMode());
      this.router.navigate([`/${tenantMatch[1]}/admin/users/add-user`], {
        queryParams: queryParams ?? undefined,
      });
    } else {
      // No tenant ID found - this shouldn't happen in production
      // Could add error handling here
    }
  }

  /**
   * Permission checking methods
   */
  hasUsersAddEditPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.USERS_CREATE_UPDATE_ALL]);
  }
}
